import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";

import { ApiError } from "./api/failures";
import { SessionProvider, useSession } from "./features/auth/session";
import { LocaleProvider } from "./features/i18n/locale";
import { routeTree } from "./routeTree.gen";
import "./styles.css";
import { rememberAcquisition } from "./features/auth/acquisition";
import { listenForInstall } from "./features/pwa/install";

const maxRetries = 2;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failures, error) => failures < maxRetries && !(error instanceof ApiError && error.status < 500),
    },
  },
});

const router = createRouter({
  routeTree,
  context: { session: { status: "loading" }, queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const session = useSession();
  useEffect(() => {
    // Before the session is known the guards would send everyone to sign in, losing the address they opened.
    if (session.status === "loading") {
      return;
    }
    if (session.status === "signedOut") {
      queryClient.clear();
      // The tab's drafts, such as answers to the voice questions, belong to the person who left.
      sessionStorage.clear();
    }
    void router.invalidate();
  }, [session]);
  if (session.status === "loading") {
    return null;
  }
  return <RouterProvider router={router} context={{ session, queryClient }} />;
}

rememberAcquisition(window.location.search);

const root = document.getElementById("root");
if (root !== null) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <LocaleProvider>
            <App />
          </LocaleProvider>
        </SessionProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// The browser offers to install the app once, at the first load: it is kept for the offer after the first cycle.
listenForInstall();

// The service worker shows the pushes; a browser without one simply receives none.
if ("serviceWorker" in navigator) {
  void navigator.serviceWorker.register("/sw.js");
}
